import { formatMoneyString } from "@/composables/formatUtils";
import { groupBy, map, sortBy } from "lodash-es";
import type { LedgerAmount, LedgerEntry } from "./ledgerTypes";

// The backend's ledger unit: 10^-7 of the major unit (app/money/ledger/money.py LEDGER_ASSET_SCALE).
const LEDGER_SCALE = 7;
const ACRONYMS = new Set(["VAT", "FX", "GO", "ECB"]);
// The "ITEM_TYPE: " prefix dual-write puts before an invoice item's own description.
const ITEM_TYPE_PREFIX = /^[A-Z][A-Z_]*: /;

/**
 * One currency's company balance split by entry state, exact decimal strings in the ledger's sign.
 *
 * @property awaitingPayment - The balance less open and held: settled entries on invoices not yet
 *   paid, net of payments, plus any entry outside the obligation states
 */
export interface BalanceBreakdown {
  currency: string;
  balance: string;
  open: string;
  openCount: number;
  held: string;
  heldCount: number;
  awaitingPayment: string;
}

/** Formats an exact decimal string with an explicit sign: "+1 240.00", "−18.60", zero unsigned. */
export function formatSignedAmount(amount: string): string {
  const sign = amountSign(amount);
  const prefix = sign > 0 ? "+" : sign < 0 ? "−" : "";
  return prefix + formatUnsignedAmount(amount);
}

export function formatUnsignedAmount(amount: string): string {
  return formatMoneyString(amount.replace(/^-/, ""));
}

export function amountSign(amount: string): -1 | 0 | 1 {
  const units = toUnits(amount);
  return units > 0n ? 1 : units < 0n ? -1 : 0;
}

/** Exact per-currency totals, sorted by currency; amounts in different currencies are never added. */
export function sumByCurrency(
  rows: { currency: string; amount: string }[]
): LedgerAmount[] {
  const totals = map(groupBy(rows, "currency"), (group, currency) => ({
    currency,
    amount: fromUnits(
      group.reduce((total, row) => total + toUnits(row.amount), 0n)
    ),
  }));
  return sortBy(totals, "currency");
}

/** Splits each currency's balance into what is open, what is held back and the rest. */
export function balanceBreakdown(
  balances: LedgerAmount[],
  entries: Pick<LedgerEntry, "currency" | "company_balance" | "state">[]
): BalanceBreakdown[] {
  return balances.map(({ currency, amount }) => {
    const inCurrency = entries.filter((entry) => entry.currency === currency);
    const open = inCurrency.filter((entry) => entry.state === "open");
    const held = inCurrency.filter((entry) => entry.state === "held");
    const openUnits = sumUnits(open);
    const heldUnits = sumUnits(held);
    return {
      currency,
      balance: amount,
      open: fromUnits(openUnits),
      openCount: open.length,
      held: fromUnits(heldUnits),
      heldCount: held.length,
      awaitingPayment: fromUnits(toUnits(amount) - openUnits - heldUnits),
    };
  });
}

/**
 * Formats a part of a total from the total's side: unsigned when it runs the same way as the total
 * (or is zero), with a minus when it runs against it.
 */
export function alignedAmount(part: string, total: string): string {
  const against = amountSign(part) * amountSign(total) < 0;
  return (against ? "\u2212" : "") + formatUnsignedAmount(part);
}

/** An enum name in words: "OPENING_EQUITY" → "Opening equity", acronyms kept upper case. */
export function humanize(value: string): string {
  const words = value
    .split("_")
    .map((word) => (ACRONYMS.has(word) ? word : word.toLowerCase()));
  const [first = "", ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
}

/** An entry's description without the item type prefix its kind already says. */
export function entryDescription(description: string): string {
  return description.replace(ITEM_TYPE_PREFIX, "");
}

function sumUnits(entries: Pick<LedgerEntry, "company_balance">[]): bigint {
  return entries.reduce(
    (total, entry) => total + toUnits(entry.company_balance),
    0n
  );
}

function toUnits(amount: string): bigint {
  const negative = amount.startsWith("-");
  const [whole, fraction = ""] = (negative ? amount.slice(1) : amount).split(
    "."
  );
  const units = BigInt(whole + fraction.padEnd(LEDGER_SCALE, "0"));
  return negative ? -units : units;
}

// Mirrors Money.to_decimal: at least two decimals, no trailing zeros beyond them.
function fromUnits(units: bigint): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units)
    .toString()
    .padStart(LEDGER_SCALE + 1, "0");
  const whole = digits.slice(0, -LEDGER_SCALE);
  const fraction = digits
    .slice(-LEDGER_SCALE)
    .replace(/0+$/, "")
    .padEnd(2, "0");
  return `${negative ? "-" : ""}${whole}.${fraction}`;
}
